import React from "react";
import { useLazyLoadQuery, useRelayEnvironment } from "react-relay";

import cx from "classnames";
import { graphql } from "relay-runtime";

import { useNewOnboardingFlow } from "~/lib/features";
import { commitMutationPromise } from "~/lib/relayUtil";
import AutoRecordingSettingsSelector, { type UIAutoRecordSetting } from "~/settings/AutoRecordingSettingsSelector";
import RecapSettings from "~/settings/RecapSettings";
import type { AttendeeValueOptions } from "~/settings/RecapSettingsSelector";
import {
	mapUIAttendeeValueToUserMeetingRecapSendTo,
	mapUserMeetingRecapSendToToUIAttendeeValue,
} from "~/settings/RecapSettingsSelector";
import {
	getLockDefaultAutoRecordAndShareSettings,
	mapMeetingOwnershipFilterToUIAutoRecordSetting,
} from "~/settings/SettingsModal/MeetingCopilot";
import { Button, Checkbox, toast, Tooltip } from "~/ui";
import { Link } from "~/ui/legacyFellowIndex";

import mockMeetingImg from "./css/meeting-mock.png";
import { JourneyContext } from "./JourneyContext";

import type { aiPageQuery } from "~/__generated__/aiPageQuery.graphql";
import type { aiPageSaveSettingsMutation } from "~/__generated__/aiPageSaveSettingsMutation.graphql";

import settingsStyles from "~/settings/css/settings.module.css";

import styles from "./css/aiPage.module.css";

export const AI_PATH = "/o/ai/";

const AUTO_RECORD_QUESTION = {
	name: "auto-record",
	title: "Meetings the Note Taker will join",
	choices: [
		{
			value: "all",
			label: <>Only meetings with a video call link</>,
		},
		{
			value: "organized",
			label: <>Only meetings that I own</>,
		},
		{
			value: "off",
			label: <>All meetings I invite Fellow to join</>,
		},
	],
};

// for accounts where allowUserAutoRecord is disabled
const AUTO_RECORD_QUESTION_WITH_AUTO_RECORD_DISABLED = {
	name: "auto-record",
	title: "Meetings the Note Taker will join",
	choices: [
		{
			value: "off",
			label: <>All meetings I invite Fellow to join</>,
		},
	],
};

const AUTO_SHARE_QUESTION = {
	name: "auto-share",
	title: "Send recaps to",
	choices: [
		{
			value: "internal-attendees",
			label: <>Me and participants from my team</>,
		},
		{
			value: "all",
			label: <>Everyone on the invite</>,
		},
		{
			value: "just-me",
			label: <>Just me</>,
		},
	],
};
const AUTO_SHARE_QUESTION_WITHOUT_ALL = {
	...AUTO_SHARE_QUESTION,
	choices: AUTO_SHARE_QUESTION.choices.filter(choice => choice.value !== "all"),
};

/**
 * get the initial value for auto-record based on the user's account default settings,
 * and the environment the app is on. We use this to set the initial value for auto-record in onboarding.
 *
 * If the billing feature hasAccessToDefaultUserConfigurations is not enabled:
 * For non-prod (mostly staging and sandboxes) environments we default to "off" for auto-record
 *because defaulting to "all" results in a lot of bots from those environments joining meetings
 */
export function getInitialAccountUserAutoRecordValue(account: aiPageQuery["response"]["account"]) {
	const { billingAccount, defaultUserAutoRecordSettings } = account;
	const isProd = window.ENVIRONMENT === "prod";

	if (!billingAccount.hasAccessToDefaultUserConfigurations) {
		return isProd ? "all" : "off";
	}

	// Account has the default set to "off"
	if (defaultUserAutoRecordSettings?.enabled === false) {
		return "off";
	}

	// get the type of recording default we want to use based on meetingOwnershipFilter
	const defaultRecordingValue = mapMeetingOwnershipFilterToUIAutoRecordSetting(
		defaultUserAutoRecordSettings.meetingOwnershipFilter,
	);

	// if the account has locked the settings, return the default value. This is the only case in which we will show
	// a non "off" default value on staging/sandbox environments.
	if (defaultUserAutoRecordSettings.allowUsersToChangeAutoRecordSettings === false) {
		return defaultRecordingValue;
	}
	// Return the account default setting on production, but on non prod environments, use "off" as default.
	return isProd ? defaultRecordingValue : "off";
}

/**
 * get the initial values for auto-share on onboarding.
 *
 * If the billing feature for hasAccessToDefaultUserConfigurations is enabled,
 * will return a tuple of the sendTo and alwaysEmailWhenAttendee values based on the account default settings
 *
 * If  not enabled, the default sendTo is "all" if public recap is allowed, otherwise it is "internal-attendees".
 * The default alwaysEmailWhenAttendee is true
 */
export function getInitialAccountUserAutoShareValue(
	account: aiPageQuery["response"]["account"],
): [sendTo: AttendeeValueOptions, lockSendTo: boolean] {
	const { billingAccount, defaultUserMeetingRecapAutoShareSettings } = account;

	const defaultSendTo = account.allowPublicRecap
		? mapUserMeetingRecapSendToToUIAttendeeValue["ALL"]
		: mapUserMeetingRecapSendToToUIAttendeeValue["INTERNAL"];
	const defaultAlwaysEmailWhenAttendee = true;

	if (billingAccount.hasAccessToDefaultUserConfigurations) {
		const initialSendTo = defaultUserMeetingRecapAutoShareSettings?.sendTo
			? mapUserMeetingRecapSendToToUIAttendeeValue[defaultUserMeetingRecapAutoShareSettings.sendTo]
			: defaultSendTo;
		const initialAlwaysEmailWhenAttendee =
			defaultUserMeetingRecapAutoShareSettings?.alwaysEmailWhenAttendee || defaultAlwaysEmailWhenAttendee;
		return [initialSendTo, initialAlwaysEmailWhenAttendee];
	}

	return [defaultSendTo, defaultAlwaysEmailWhenAttendee];
}

export function AIPage() {
	const { account } = useLazyLoadQuery<aiPageQuery>(
		graphql`
			query aiPageQuery {
				account {
					allowPublicRecap
					allowUserAutoRecord
					emailDomainList
					defaultUserAutoRecordSettings {
						enabled
						meetingOwnershipFilter
						allowUsersToChangeAutoRecordSettings
					}
					defaultUserMeetingRecapAutoShareSettings {
						sendTo
						alwaysEmailWhenAttendee
						allowUsersToChangeSendTo
						allowUsersToChangeAlwaysEmailWhenAttendee
					}
					billingAccount {
						hasAccessToDefaultUserConfigurations: hasAccessToFeature(feature: default_user_configurations)
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-or-network" },
	);
	const isNewOnboardingFlow = useNewOnboardingFlow();
	const relayEnv = useRelayEnvironment();
	const { nextStep } = React.useContext(JourneyContext);

	const initialAccountUserAutoRecordValue = React.useMemo(() => {
		return getInitialAccountUserAutoRecordValue(account);
	}, [account]);

	const [initialAccountUserAutoShareSendTo, initialAccountUserAutoShareAlwaysEmailWhenAttendee] = React.useMemo(() => {
		return getInitialAccountUserAutoShareValue(account);
	}, [account]);

	const [questionAnswers, setQuestionAnswers] = React.useState<Record<string, string>>({
		// For non-prod (mostly staging and sandboxes) environments we default to "off" for auto-record
		// because defaulting to "all" results in a lot of bots from those environments joining meetings
		"auto-record": initialAccountUserAutoRecordValue,
		"auto-share": initialAccountUserAutoShareSendTo,
	});
	const [alwaysEmailWhenAttendee, setAlwaysEmailWhenAttendee] = React.useState(
		initialAccountUserAutoShareAlwaysEmailWhenAttendee,
	);

	// New state for the experiment
	const [emailOptin, setEmailOptin] = React.useState(true);
	const [acceptedTermsOfService, setAcceptedTermsOfService] = React.useState(false);

	const handleAlwaysEmailWhenAttendeeChange = React.useCallback(() => {
		setAlwaysEmailWhenAttendee(prev => !prev);
	}, []);

	const onEmailOptinChange = React.useCallback(() => {
		setEmailOptin(prev => !prev);
	}, []);

	const onAcceptedTermsOfServiceChange = React.useCallback(() => {
		setAcceptedTermsOfService(prev => !prev);
	}, []);

	const [lockAutoRecord, lockSendTo, lockAlwaysEmailWhenAttendee] = React.useMemo(() => {
		return getLockDefaultAutoRecordAndShareSettings(account);
	}, [account]);

	const onSubmit = React.useCallback(() => {
		const autoRecordEnabled = questionAnswers["auto-record"] !== "off";
		const autoShareSendTo =
			mapUIAttendeeValueToUserMeetingRecapSendTo[questionAnswers["auto-share"] as AttendeeValueOptions] ||
			mapUIAttendeeValueToUserMeetingRecapSendTo["internal-attendees"];

		commitMutationPromise<aiPageSaveSettingsMutation>(relayEnv, {
			mutation: graphql`
				mutation aiPageSaveSettingsMutation(
					$autoRecordOwnership: FilterMeetingOwnership!
					$autoRecordEnabled: Boolean!
					$autoShareSendTo: UserMeetingRecapSendTo!
					$alwaysEmailWhenAttendee: Boolean!
					$notAcceptedTermsOfService: Boolean!
				) {
					updateUserAutoRecordSettings(meetingOwnershipFilter: $autoRecordOwnership, enabled: $autoRecordEnabled) {
						settings {
							id
							enabled
							meetingOwnershipFilter
						}
					}
					updateUserMeetingRecapAutoShareSettings(
						sendTo: $autoShareSendTo
						alwaysEmailWhenAttendee: $alwaysEmailWhenAttendee
					) {
						settings {
							id
							sendTo
							alwaysEmailWhenAttendee
						}
					}
					acceptTermsOfService(input: {}) @skip(if: $notAcceptedTermsOfService) {
						user {
							id
						}
					}
				}
			`,
			variables: {
				autoRecordEnabled,
				autoRecordOwnership: questionAnswers["auto-record"] === "all" ? "ALL_MEETINGS" : "ORGANIZED_BY_ME",
				autoShareSendTo,
				alwaysEmailWhenAttendee,
				notAcceptedTermsOfService: !(isNewOnboardingFlow && acceptedTermsOfService),
			},
		})
			.then(res => {
				if (!res.updateUserAutoRecordSettings || !res.updateUserMeetingRecapAutoShareSettings) {
					toast.error("Failed to save settings. Don't worry, you can change it later in user settings.");
				}
			})
			.catch(() => {
				toast.error("Failed to save settings. Don't worry, you can change it later in user settings.");
			})
			.finally(() => {
				// Let JourneyContext handle the next step logic, including any variant-specific behavior
				nextStep({
					source: AI_PATH,
					val: questionAnswers["auto-record"],
				});
			});
	}, [questionAnswers, alwaysEmailWhenAttendee, relayEnv, nextStep, isNewOnboardingFlow, acceptedTermsOfService]);

	const questions = React.useMemo(
		() => [
			account.allowUserAutoRecord ? AUTO_RECORD_QUESTION : AUTO_RECORD_QUESTION_WITH_AUTO_RECORD_DISABLED,
			account.allowPublicRecap ? AUTO_SHARE_QUESTION : AUTO_SHARE_QUESTION_WITHOUT_ALL,
		],
		[account.allowPublicRecap, account.allowUserAutoRecord],
	);
	const canContinue =
		Object.keys(questionAnswers).length === questions.length && (!isNewOnboardingFlow || acceptedTermsOfService);

	const handleAutoRecordChange = React.useCallback(
		(value: UIAutoRecordSetting) => {
			setQuestionAnswers(existing => ({ ...existing, "auto-record": value }));
		},
		[setQuestionAnswers],
	);

	const handleAutoShareChange = React.useCallback(
		(value: AttendeeValueOptions) => {
			setQuestionAnswers(existing => ({ ...existing, "auto-share": value }));
		},
		[setQuestionAnswers],
	);

	return (
		<div className={styles.page}>
			<div className={styles.pageLeft}>
				<div className={styles.titleExperiment}>Automate your AI meeting notes</div>
				<div className={styles.questionsExperiment}>
					<div className={styles.question}>
						<div className={styles.questionTitleExperiment}>Fellow will join and take AI meeting notes for:</div>
						<AutoRecordingSettingsSelector
							value={questionAnswers["auto-record"] as UIAutoRecordSetting}
							onChange={handleAutoRecordChange}
							lockToValue={lockAutoRecord}
						/>
					</div>
					<div className={styles.question}>
						<div className={styles.questionTitleExperiment}>Email AI meeting notes to:</div>
						<RecapSettings
							value={questionAnswers["auto-share"] as AttendeeValueOptions}
							onChange={handleAutoShareChange}
							alwaysEmailWhenAttendee={alwaysEmailWhenAttendee}
							onAlwaysEmailWhenAttendeeChange={handleAlwaysEmailWhenAttendeeChange}
							emailDomainList={account.emailDomainList}
							showNoneInfoBubble={false}
							lockToSendToValue={lockSendTo}
							lockAlwaysEmailWhenAttendee={lockAlwaysEmailWhenAttendee}
						/>
					</div>
				</div>
				<div className={styles.actions}>
					{isNewOnboardingFlow ? (
						<Tooltip
							content={
								<div className={styles.tooltipContent}>
									You must agree to the Terms of Service and Privacy Policy to continue
								</div>
							}
							disabled={acceptedTermsOfService}
						>
							<div>
								<Button
									className={styles.continueBtn}
									variant="primary"
									onClick={onSubmit}
									disabled={!canContinue}
									data-test-id="ai-onboarding-continue-btn"
								>
									Continue
								</Button>
							</div>
						</Tooltip>
					) : (
						<Button
							className={styles.continueBtn}
							variant="primary"
							onClick={onSubmit}
							disabled={!canContinue}
							data-test-id="ai-onboarding-continue-btn"
						>
							Continue
						</Button>
					)}
				</div>

				{isNewOnboardingFlow && (
					<div className={styles.checkboxGroup}>
						<div className={cx(settingsStyles.checkboxContainer, settingsStyles.checkboxContainerWithTooltip)}>
							<Checkbox
								id="acceptedTermsOfService"
								checked={acceptedTermsOfService}
								onCheckedChange={onAcceptedTermsOfServiceChange}
								className={settingsStyles.checkbox}
							/>
							<label htmlFor="acceptedTermsOfService" className={settingsStyles.label}>
								I agree to the{" "}
								<Link href="https://fellow.app/terms-of-use" target="_blank">
									Terms of Service
								</Link>{" "}
								and{" "}
								<Link href="https://fellow.app/privacy-policy" target="_blank">
									Privacy Policy
								</Link>
							</label>
						</div>

						<div className={cx(settingsStyles.checkboxContainer, settingsStyles.checkboxContainerWithTooltip)}>
							<Checkbox
								id="emailOptin"
								checked={emailOptin}
								onCheckedChange={onEmailOptinChange}
								className={settingsStyles.checkbox}
							/>
							<label htmlFor="emailOptin" className={settingsStyles.label}>
								Please send me helpful product updates
							</label>
						</div>
					</div>
				)}

				<div style={{ height: "20px" }} />
				<p className={styles.subtleText}>You can change this later in settings</p>
			</div>
			<div className={styles.pageRight}>
				<img src={mockMeetingImg} alt="Video conferencing" />
			</div>
		</div>
	);
}
